import { createContext, FC, useContext, useMemo } from 'react';
import { AiOutlinePlus } from 'react-icons/ai';
import { Box, Button, Flex, Menu, Portal, Spacer } from '@chakra-ui/react';

import { useApplicationDocumentStates } from '~/core/shared/api/middle-office/application/hooks/useApplicationDocumentStates';
import { FeatureKey, Module } from '~/core/shared/constants';
import { useTenantedApplicationConfig } from '~/core/shared/contexts';
import { useFeature } from '~/core/shared/contexts/features';
import { Permissions } from '~/core/shared/generated/Permissions';
import { useRequiredParams } from '~/core/shared/hooks';
import { useCan } from '~/core/shared/hooks/useCan';
import { DocumentStateResource } from '~/core/shared/types';
import {
  SummaryTabBlockConfig,
  TenantDocumentRouteProps,
} from '../../../config';
import { useMiddleOfficeConfig } from '../../../contexts/middleOfficeConfig';
import { ApplicationAssignees } from '../../ApplicationAssignees';
import { DocumentStatusSelect } from '../../DocumentStatusSelect.component';
import { LayoutContext } from '../../Layout.component';
import { EmailCreate } from '../../messages/EmailCreate.component';
import { NoteCreate } from '../../messages/NoteCreate.component';
import { Status } from '../../table/Status.component';
import { Workbook } from '../../workbooks/Workbook.component';
import { ApplicationContext } from '../Layout.component';
import { SummaryContentComponent } from '../summary';

type SummaryTabElementConfig = any;

type SummaryTabConfig = {
  blocks?: Record<string, SummaryTabBlockConfig>;
  elements?: Record<string, SummaryTabElementConfig>;
  controls?: boolean;
};

type SummaryTabProps = {
  summaryTemplateName: string;
  summaryConfig?: SummaryTabConfig;
} & TenantDocumentRouteProps;

type SummaryTabContextType = {
  getBlockConfig: (block: string) => SummaryTabBlockConfig;
  getElementConfig: (element: string) => SummaryTabElementConfig;
};

type SidebarContentTypes = 'note' | 'email' | 'workbook';

export const SummaryTabContext = createContext<SummaryTabContextType>(
  {} as SummaryTabContextType,
);

export const SummaryTab: FC<SummaryTabProps> = ({
  summaryTemplateName,
  summaryConfig,
  tenantDocumentDefinition,
}) => {
  const config = useTenantedApplicationConfig();
  const { can, canAll, canAny } = useCan();
  const { uuid } = useRequiredParams(['uuid']);
  const { status, definition } = useContext(ApplicationContext);
  const middleOfficeConfig = useMiddleOfficeConfig();
  const {
    openSidebarModalComponent,
    sidebarModalOpen,
    closeSidebarModalComponent,
  } = useContext(LayoutContext);

  const workbooksEnabled = useFeature(FeatureKey.Workbooks).enabled;

  const openNoteSidebar = (typeSidebar: SidebarContentTypes): void => {
    if (typeSidebar === 'note') {
      openSidebarModalComponent(
        'Add private note',
        <NoteCreate
          cancel={closeSidebarModalComponent}
          documentDefinition={definition}
          documentId={uuid}
        />,
      );
    } else if (typeSidebar === 'email') {
      openSidebarModalComponent(
        `Mail to ${middleOfficeConfig.getLabel('messageTab', 'mailTo', 'customer')}`,
        <EmailCreate
          cancel={closeSidebarModalComponent}
          documentDefinition={definition}
          documentId={uuid}
        />,
      );
    } else if (typeSidebar === 'workbook' && workbooksEnabled) {
      openSidebarModalComponent(
        'Workbook',
        <Workbook
          cancel={closeSidebarModalComponent}
          documentDefinition={definition}
          documentId={uuid}
        />,
      );
    }
  };

  const getBlockConfig = useMemo(
    () =>
      (block: string): SummaryTabBlockConfig => {
        let blockConfig: SummaryTabBlockConfig = {};
        if (summaryConfig && summaryConfig.blocks) {
          blockConfig = summaryConfig.blocks[block] || blockConfig;
        }
        return blockConfig;
      },
    [summaryConfig],
  );

  const getElementConfig = useMemo(
    () =>
      (element: string): SummaryTabElementConfig => {
        let elementConfig: SummaryTabElementConfig = {};
        if (summaryConfig && summaryConfig.elements) {
          elementConfig = summaryConfig.elements[element] || elementConfig;
        }
        return elementConfig;
      },
    [summaryConfig],
  );

  const { modules } = config.documents[tenantDocumentDefinition];
  const { primaryWorkflow } = modules[Module.MiddleOffice];
  const { priorityWorkflow } = modules[Module.MiddleOffice];

  const {
    documentStates: documentStatesArray,
    refetch: refetchDocumentStates,
  } = useApplicationDocumentStates(
    uuid,
    tenantDocumentDefinition,
    [primaryWorkflow.workflowDefinition, priorityWorkflow.workflowDefinition],
    status,
  );

  const documentStates = useMemo(
    () =>
      Object.fromEntries(
        (documentStatesArray ?? []).map((r) => [r.workflow, r]),
      ) as Record<DocumentStateResource['workflow'], DocumentStateResource>,
    [documentStatesArray],
  );

  const updateDocumentState = (_state: DocumentStateResource): void => {
    refetchDocumentStates();
  };

  const summaryTabContextValue = useMemo(
    () => ({
      getBlockConfig,
      getElementConfig,
    }),
    [getBlockConfig, getElementConfig],
  );

  return (
    <Flex direction="column">
      <SummaryTabContext.Provider value={summaryTabContextValue}>
        {summaryConfig?.controls && (
          <Box mb={6}>
            <Flex mb={6}>
              <Status mr={4} status={status} />

              <Spacer />

              <Flex>
                <Flex gap={4}>
                  {can(Permissions.StatusUpdate) && (
                    <>
                      <DocumentStatusSelect
                        documentDefinition={definition}
                        documentUuid={uuid}
                        onStateUpdate={updateDocumentState}
                        state={
                          documentStates[primaryWorkflow.workflowDefinition]
                        }
                        status={status}
                        workflowStatusPair={primaryWorkflow}
                      />
                      <DocumentStatusSelect
                        documentDefinition={definition}
                        documentUuid={uuid}
                        makeDirectStatusTransitions
                        onStateUpdate={updateDocumentState}
                        state={
                          documentStates[priorityWorkflow.workflowDefinition]
                        }
                        workflowStatusPair={priorityWorkflow}
                      />
                    </>
                  )}
                  <ApplicationAssignees />
                  {!sidebarModalOpen &&
                  canAny([
                    Permissions.MessagesCreate,
                    Permissions.MessagesEmailCreate,
                  ]) ? (
                    <Menu.Root>
                      <Menu.Trigger asChild>
                        <Button
                          borderColor="gray.200"
                          borderRadius="md"
                          colorPalette="gray"
                          fontSize="md"
                          fontWeight={400}
                          ml={2}
                          size="md"
                          variant="outline"
                        >
                          <AiOutlinePlus />
                          Add new
                        </Button>
                      </Menu.Trigger>
                      <Portal>
                        <Menu.Positioner>
                          <Menu.Content>
                            {canAll([Permissions.MessagesCreate]) && (
                              <Menu.Item
                                borderColor="gray.200"
                                borderRadius="md"
                                ml={2}
                                onClick={() => openNoteSidebar('note')}
                                value="note"
                                w="93%"
                              >
                                Note
                              </Menu.Item>
                            )}
                            {canAll([Permissions.MessagesEmailCreate]) && (
                              <Menu.Item
                                borderColor="gray.200"
                                borderRadius="md"
                                ml={2}
                                onClick={() => openNoteSidebar('email')}
                                value="email"
                                w="93%"
                              >
                                Email to{' '}
                                {middleOfficeConfig.getLabel(
                                  'messageTab',
                                  'mailTo',
                                  'customer',
                                )}
                              </Menu.Item>
                            )}

                            {workbooksEnabled &&
                              canAll([Permissions.WorkbooksView]) && (
                                <Menu.Item
                                  borderColor="gray.200"
                                  borderRadius="md"
                                  ml={2}
                                  onClick={() => openNoteSidebar('workbook')}
                                  value="workbook"
                                  w="93%"
                                >
                                  Workbook
                                </Menu.Item>
                              )}
                          </Menu.Content>
                        </Menu.Positioner>
                      </Portal>
                    </Menu.Root>
                  ) : null}
                </Flex>
              </Flex>
            </Flex>
          </Box>
        )}
        <SummaryContentComponent summaryTemplate={summaryTemplateName} />
      </SummaryTabContext.Provider>
    </Flex>
  );
};
